import { getMountedApps } from 'single-spa';

/**
 * Renders the menu items in the user menu flyout.
 * @param {Translator} translator Our translation tool.
 * @returns void.
 */
const _renderUserMenuFlyoutItems = (translator) => {
    const userMenuFlyout = document.getElementById('user-menu-flyout');

    if (!userMenuFlyout) {
        return;
    }

    const isBellMyReceiptsApp =
        document
            .querySelector('[data-app-slug]')
            ?.getAttribute('data-app-slug') === 'bell-my-receipts';

    const items = [
        {
            innerText: isBellMyReceiptsApp
                ? 'myProfileAndSupport'
                : 'myProfile',
            id: 'my-profile-button',
        },
        { innerText: 'support', id: 'support-button' },
        { innerText: 'logOut', id: 'log-out-button' },
    ];

    const menuItems = items.map((i) => {
        const listItem = document.createElement('li');
        listItem.setAttribute('role', 'presentation');
        listItem.setAttribute('id', i.id);
        listItem.classList.add('c-dropdown__item');

        const itemLink = document.createElement('a');
        itemLink.setAttribute('role', 'menuItem');
        itemLink.classList.add('dropdown-item', 'c-dropdown__item-link');
        itemLink.innerText = translator.translate(i.innerText);

        listItem.appendChild(itemLink);
        return listItem;
    });

    userMenuFlyout.append(...menuItems);
};

/**
 * Sets up an event listener related to logging out. Click event behaviour changes
 * depending on which apps are mounted when the logout button is clicked.
 *
 * For inspections: Clears out the cookie and local storage, and forwards the
 * user to the landing page.
 *
 * For wxPortal: Fires off a custom event for wxPortal to handle logging out on its own.
 */
const _setLogoutButtonListener = () => {
    const logOutButton = document.getElementById('log-out-button');

    logOutButton?.addEventListener('click', () => {
        const mountedApps = getMountedApps();

        if (mountedApps.includes('@weever/wxPortal')) {
            window.dispatchEvent(window.$wx.mfeLogoutEvent);
            return;
        }

        document.cookie = 'token=; Path=/; max-age=0;';
        window.localStorage.setItem('wxPortal.UserId', 'null');
        window.localStorage.setItem('wxPortal.User', 'null');
        // this is deprecated and should be purged at every opportunity
        window.localStorage.removeItem('wxPortal.UserToken');
        window.location.href = '/';
    });
};

/**
 * Routes to the appropriate path to prompt the My Profile modal.
 * @returns void.
 */
const _setMyProfileClickListener = () =>
    document
        .getElementById('my-profile-button')
        ?.addEventListener('click', () => {
            const currentRoute = window.location.pathname;
            const userProfileRoute = `/user-profile?callback=${currentRoute}`;
            const mountedApps = getMountedApps();

            if (mountedApps.includes('@weever/wxPortal')) {
                history.pushState(
                    { path: userProfileRoute },
                    '',
                    userProfileRoute
                );
            } else {
                window.location.href = userProfileRoute;
            }
        });

/**
 * Opens the Support page
 * @returns void.
 */
const _setSupportPageClickListener = () =>
    document.getElementById('support-button')?.addEventListener('click', () => {
        window.open('https://support.weeverapps.com', '_blank').focus();
    });

/**
 * Sets the click handlers to toggle the visibility of the user menu flyout.
 */
const _setUserMenuFlyoutToggle = () => {
    const userMenuFlyoutButton = document.getElementById(
        'user-menu-flyout__button'
    );
    const userMenuFlyout = document.getElementById('user-menu-flyout');

    userMenuFlyoutButton?.addEventListener('click', () => {
        userMenuFlyout?.classList.toggle('show');
    });

    window.addEventListener('click', (event) => {
        if (!userMenuFlyoutButton?.contains(event.target)) {
            userMenuFlyout?.classList.toggle('show', false);
        }
    });
};

const renderUserMenuFlyout = (translator) => {
    _setUserMenuFlyoutToggle();
    _renderUserMenuFlyoutItems(translator);
    _setMyProfileClickListener();
    _setSupportPageClickListener();
    _setLogoutButtonListener();
};

export default renderUserMenuFlyout;
